import React from 'react';
import { Row, Col, Card, Button } from 'antd';
import {
  UserOutlined,
  FilterOutlined,
  StarOutlined,
  MobileOutlined,
  ReadOutlined,
  CustomerServiceOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';


function AppFeature() {
  const features = [
    {
      id: 1,
      icon: <UserOutlined />,
      title: "Community-Specific Profiles",
      description: "Detailed profiles tailored for Brahmin individuals with subcaste, gothram, family background, and cultural preferences.",
      color: "#667eea",
      gradient: "linear-gradient(135deg, #667eea, #764ba2)",
      benefits: ["Subcaste specification", "Family tree details", "Cultural background", "Traditional values"]
    },
    {
      id: 2,
      icon: <FilterOutlined />,
      title: "Advanced Search Filters",
      description: "Powerful search tools to find matches based on subcaste, education, profession, location, and cultural preferences.",
      color: "#f093fb",
      gradient: "linear-gradient(135deg, #f093fb, #f5576c)",
      benefits: ["Subcaste filtering", "Location-based search", "Education filters", "Profession matching"]
    },
    {
      id: 3,
      icon: <StarOutlined />,
      title: "Horoscope Matching",
      description: "Traditional astrological compatibility tools including raasi, nakshatram, and dosham matching for perfect alignment.",
      color: "#4facfe",
      gradient: "linear-gradient(135deg, #4facfe, #00f2fe)",
      benefits: ["Raasi compatibility", "Nakshatram matching", "Dosham analysis", "Birth chart comparison"]
    },
    {
      id: 4,
      icon: <MobileOutlined />,
      title: "Mobile Responsive",
      description: "Seamlessly designed responsive interface that works perfectly on smartphones, tablets, and desktop devices.",
      color: "#43e97b",
      gradient: "linear-gradient(135deg, #43e97b, #38f9d7)",
      benefits: ["Mobile optimized", "Touch-friendly interface", "Fast loading", "Cross-platform support"]
    },
    {
      id: 5,
      icon: <ReadOutlined />,
      title: "Cultural Resources",
      description: "Educational content about Brahmin traditions, marriage customs, and cultural practices to enrich your knowledge.",
      color: "#fa709a",
      gradient: "linear-gradient(135deg, #fa709a, #fee140)",
      benefits: ["Traditional guides", "Marriage customs", "Cultural insights", "Community wisdom"]
    },
    {
      id: 6,
      icon: <CustomerServiceOutlined />,
      title: "Dedicated Support",
      description: "Expert customer support team that understands Brahmin community needs and provides personalized assistance.",
      color: "#a8edea",
      gradient: "linear-gradient(135deg, #a8edea, #fed6e3)",
      benefits: ["24/7 availability", "Cultural sensitivity", "Personal guidance", "Quick resolution"]
    }
  ];

  return (
    <div style={{ 
      background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)', 
      padding: '100px 0' 
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
        
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <div style={{
            display: 'inline-block',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            padding: '8px 20px',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '24px'
          }}>
            Platform Features
          </div>
          
          <h2 style={{
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '24px',
            lineHeight: '1.2'
          }}>
            Everything You Need to Find Your <span style={{ color: '#667eea' }}>Perfect Match</span>
          </h2>
          
          <p style={{
            fontSize: '1.25rem',
            color: '#6b7280',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            Discover powerful features designed specifically for the Brahmin community to make your matrimonial search meaningful and successful.
          </p>
        </div>

        {/* Features Grid */}
        <Row gutter={[32, 32]} style={{ marginBottom: '80px' }}>
          {features.map((feature, index) => (
            <Col xs={24} md={12} lg={8} key={feature.id}>
              <Card
                style={{
                  height: '100%',
                  borderRadius: '24px',
                  border: 'none',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.4s ease',
                  overflow: 'hidden',
                  cursor: 'pointer'
                }}
                bodyStyle={{ padding: 0 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-15px)';
                  e.currentTarget.style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.08)';
                }}
              >
                {/* Header with Icon */}
                <div style={{
                  background: feature.gradient,
                  padding: '40px 30px 30px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Background decoration */}
                  <div style={{
                    position: 'absolute',
                    top: '-20px',
                    right: '-20px',
                    width: '80px',
                    height: '80px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%'
                  }}></div>
                  
                  <div style={{
                    fontSize: '40px',
                    color: 'white',
                    marginBottom: '20px',
                    position: 'relative',
                    zIndex: 2
                  }}>
                    {feature.icon}
                  </div>
                  
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'white',
                    margin: 0,
                    position: 'relative',
                    zIndex: 2
                  }}>
                    {feature.title}
                  </h3>
                </div>

                {/* Content */}
                <div style={{ padding: '30px' }}>
                  <p style={{
                    color: '#6b7280',
                    lineHeight: '1.6',
                    fontSize: '16px',
                    marginBottom: '24px'
                  }}>
                    {feature.description}
                  </p>

                  {/* Benefits List */}
                  <div style={{ marginBottom: '24px' }}>
                    {feature.benefits.map((benefit, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <CheckCircleOutlined style={{
                          color: feature.color,
                          fontSize: '16px',
                          marginRight: '10px'
                        }} />
                        <span style={{
                          color: '#4b5563',
                          fontSize: '14px'
                        }}>
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Learn More Link */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: feature.color,
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                  >
                    Explore Feature
                    <ArrowRightOutlined style={{ marginLeft: '8px', fontSize: '12px' }} />
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Bottom CTA Section */}
        <div style={{
          background: 'white',
          borderRadius: '32px',
          padding: '60px 40px',
          textAlign: 'center',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '16px'
          }}>
            Ready to Experience These Features?
          </h3>
          
          <p style={{
            fontSize: '1.2rem',
            color: '#6b7280',
            marginBottom: '40px',
            maxWidth: '600px',
            margin: '0 auto 40px'
          }}>
            Join thousands of Brahmin singles who are already using these powerful features to find their life partners.
          </p>

          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Button
              type="primary"
              size="large"
              style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                border: 'none',
                borderRadius: '25px',
                height: '56px',
                padding: '0 32px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              Start Free Today
              <ArrowRightOutlined style={{ marginLeft: '8px' }} />
            </Button>
            
            <Button
              size="large"
              style={{
                borderColor: '#667eea',
                color: '#667eea',
                borderRadius: '25px',
                height: '56px',
                padding: '0 32px',
                fontSize: '16px',
                fontWeight: '500',
                background: 'transparent'
              }}
            >
              View Demo
            </Button>
          </div>

          {/* Trust Indicators */}
          <div style={{
            marginTop: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '32px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              color: '#6b7280',
              fontSize: '14px'
            }}>
              <CheckCircleOutlined style={{ color: '#10b981', marginRight: '8px' }} />
              100% Free to Join
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              color: '#6b7280',
              fontSize: '14px'
            }}>
              <CheckCircleOutlined style={{ color: '#10b981', marginRight: '8px' }} />
              Safe & Secure
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              color: '#6b7280',
              fontSize: '14px'
            }}>
              <CheckCircleOutlined style={{ color: '#10b981', marginRight: '8px' }} />
              No Hidden Charges
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default AppFeature;